import { useQuery } from '@tanstack/react-query';

import { getAvailableClasses } from '@/services/class';
import { Class } from './class-groups';
import {
  AVAILABILITY_STALE_MS,
  type SubstituteInstructor,
} from './class-availabilities';

interface Slot {
  slot_id: string | null;
  override_slot_id: string | null;
  start_time: number;
  end_time: number;
  timezone: string;
  available_spots: number | null;
  total_spots: number | null;
  is_fully_booked?: boolean;
  waitlist_enabled?: boolean;
  waitlist_count?: number;
}

interface Instructor {
  admin_user_id: string;
  first_name: string;
  last_name: string;
  profile_picture: string | null;
}

export interface AvailableClass extends Class {
  slot: Slot;
  instructor: Instructor;
  instructor_schedule_id: string;
  is_substituted?: boolean;
  original_instructor?: SubstituteInstructor | null;
}

export type AvailableDay = {
  date: string;
  classes: AvailableClass[];
  has_classes?: boolean;
};

export type ClassDailyDates = {
  days: AvailableDay[];
  next_week_days?: AvailableDay[];
};

export default function useAvailableClasses({
  start_date,
  end_date,
  timezone,
  response_mode,
}: {
  start_date: string;
  end_date: string;
  timezone: string;
  response_mode?: 'full' | 'summary';
}) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'available_classes',
      start_date,
      end_date,
      timezone,
      response_mode,
    ],
    queryFn: () =>
      getAvailableClasses({ start_date, end_date, timezone, response_mode }),
    enabled: !!start_date && !!end_date && !!timezone,
    staleTime: AVAILABILITY_STALE_MS,
    refetchOnWindowFocus: true,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
}
